import { AnyAction } from 'redux';
import { PaginatedResponse } from '@synergy/api';
import { defaultPageSize } from '@synergy/config';

import {
    CREATE_EMPLOYEE_COMPLETED,
    DELETE_EMPLOYEE_SUCCESS,
    DELETE_USER,
    EMAIL_EXISTS_WARNING,
    RECEIVE_EMPLOYEES,
    REQUEST_EMPLOYEES,
    START_CREATE_EMPLOYEE,
    START_UPDATE_EMPLOYEE,
    UPDATE_EMPLOYEE_COMPLETED,
    UPDATE_IMPORT_CONTACT,
    UPDATE_MAIN_CONTACT,
} from '../actions/user-management-actions';
import { Employee } from '../models/employee';

export interface UserManagementReducerState {
    employeeData: PaginatedResponse<Employee>;
    requestingUser: boolean;
    isExistingEmail: boolean;
}

const initialUserManagementState: UserManagementReducerState = {
    employeeData: { items: [], total: 0, page: 0, limit: defaultPageSize },
    requestingUser: false,
    isExistingEmail: false,
};

export const userManagementReducer = (
    state: UserManagementReducerState = initialUserManagementState,
    action: AnyAction
): UserManagementReducerState => {
    const { type, ...rest } = action;
    switch (type) {
        case REQUEST_EMPLOYEES:
        case START_CREATE_EMPLOYEE:
        case START_UPDATE_EMPLOYEE: {
            return {
                ...state,
                requestingUser: true,
                isExistingEmail: false,
            };
        }

        case DELETE_EMPLOYEE_SUCCESS: {
            const paginatedUsers = { ...state.employeeData };
            const users = paginatedUsers.items.filter(user => user.id !== rest['userId']);

            if (rest['reassignMainContactId']) {
                const mainContactIndex = paginatedUsers.items.findIndex(
                    user => user.id === rest['reassignMainContactId']
                );
                if (mainContactIndex >= 0) {
                    paginatedUsers.items[mainContactIndex].isMainContact = true;
                }
            }
            if (rest['reassignBonusContactId']) {
                const bonusContactIndex = paginatedUsers.items.findIndex(
                    user => user.id === rest['reassignBonusContactId']
                );
                if (bonusContactIndex >= 0) {
                    paginatedUsers.items[bonusContactIndex].hasBonusConditions = true;
                }
            }
            if (rest['reassignImportContactId']) {
                const importContactIndex = paginatedUsers.items.findIndex(
                    user => user.id === rest['reassignImportContactId']
                );
                if (importContactIndex >= 0) {
                    paginatedUsers.items[importContactIndex].isImportContact = true;
                }
            }

            paginatedUsers.items = users;
            paginatedUsers.total--;

            return {
                ...state,
                employeeData: paginatedUsers,
                requestingUser: false,
            };
        }

        case DELETE_USER: {
            const paginatedUsers = { ...state.employeeData };
            const userIndex = paginatedUsers.items.findIndex(user => user.id === rest['userId']);
            if (userIndex < 0) {
                return state;
            }
            const user = { ...paginatedUsers.items[userIndex] };
            user.user = undefined;
            paginatedUsers.items[userIndex] = user;

            return {
                ...state,
                employeeData: paginatedUsers,
            };
        }

        case CREATE_EMPLOYEE_COMPLETED: {
            const paginatedUsers = { ...state.employeeData };

            paginatedUsers.items.unshift(rest['user']);
            paginatedUsers.total++;

            if (paginatedUsers.items.length > paginatedUsers.limit) {
                paginatedUsers.items.pop();
            }

            return {
                ...state,
                employeeData: paginatedUsers,
                requestingUser: false,
            };
        }

        case UPDATE_MAIN_CONTACT: {
            const paginatedUsers = { ...state.employeeData };

            paginatedUsers.items = paginatedUsers.items.map(item => {
                return {
                    ...item,
                    isMainContact: item.id === rest['mainContactId'],
                };
            });

            return {
                ...state,
                employeeData: paginatedUsers,
            };
        }

        case UPDATE_IMPORT_CONTACT: {
            const paginatedUsers = { ...state.employeeData };

            paginatedUsers.items = paginatedUsers.items.map(item => {
                return {
                    ...item,
                    isImportContact: item.id === rest['importContactId'],
                };
            });

            return {
                ...state,
                employeeData: paginatedUsers,
            };
        }

        case UPDATE_EMPLOYEE_COMPLETED: {
            return {
                ...state,
                requestingUser: false,
            };
        }

        case RECEIVE_EMPLOYEES: {
            return {
                ...state,
                employeeData: rest['paginatedUsers'],
                requestingUser: false,
            };
        }

        case EMAIL_EXISTS_WARNING: {
            return {
                ...state,
                isExistingEmail: true,
                requestingUser: false,
            };
        }

        default: {
            return state;
        }
    }
};
